import type { Database, DataReader, DocumentData, Owner } from "./Database";
import { documentIDChars, globalSettingsCollectionName } from "./database-strings";
import { getFeatureFlag } from "./feature-flags";

// We have these default feature settings, and we're listening to them
// from Firestore.  We also put them into `window` in the `play` function,
// and we save them in local storage.  The reason we don't completely
// rely on the `play` function is that iOS will cache the results of `play`
// until we redeploy, so we couldn't change the feature settings unless
// we deploy again.

// NOTE: Don't change the type of any of these, or our local storage
// saving will break.
const defaultFeatureSettings = {
    debugActions: false,
    newComponentsForNewColumns: false,
    emailSharing: false,
    developerBroadcastNotifications: false,
    logGeocodes: false,
    periodicRefresh: false,
    serverSizedImages: false,
    preTileScreens: false,
    unsplash: false,
    imageGravity: false,
    showMissingEntities: false,
    myBilling: false,
    orgBillingReminders: false,
    flyio: false,
    likeComponent: false,
    textBox: false,
    simpleTable: false,
    templateOnboarding: false,
    templateSubmit: false,
    phoneNumberInBuyScreen: false,
    webviewComponent: false,
    cloudinaryGifs: false,
    driveImageDirect: false,
    cloudinaryUploadDrive: false,
    durableActionPosting: false,
    dragDropColumns: false,
    locationEntry: false,
    incrementAction: false,
    fontThemes: false,
    signatureComponent: false,
    uglyFingerCursor: false,
    tabVisibility: false,
    reactionComponent: false,
    inYourFaceBranding: false,
    useBackendSnapshots: false,
    boosts: false,
    containingScreenColumns: false,
    grouping: false,
    reloadQuota: false,
    directBuilderInteraction: false,
    referralLinks: false,
    usageScreen: false,
    signInAction: false,
    nativeTables: false,
    attemptiOSPWABackgroundAudio: false,
    sideBySide: false,
    deepLinks: false,
    proxyUploadsViaSameOrigin: false,
    signatureQuota: false,
    // We've had issues with the frontend writing incorrectly encoded strings
    // to the backend. We'll mitigate this with base64 encoding. Since this
    // is a major change, we need the ability to roll it back instantly.
    // Hence the feature setting.
    base64EncodeBuilderApp: false,
    offsetInSingleValue: false,
    generateImageColumn: false,
    cloudFunctionCrossOriginProbability: 0.1,
    usePlayerCriticalContainer: true,
    usePlayerSmallContainer: true,
    usePlayerLargeContainer: true,
    useBuilderSmallContainer: true,
    useBuilderLargeContainer: true,
    useBuilderFunctionsProxy: false,
    useFunctionsProxy: true,
    traceImageLoadProbability: 0.05,
    traceLazyLoadingProbability: 0.05,
    eventPicker: false,
    compileFormulaToJS: false,
    roles: false,
    protectedColumns: false,
    cleanupFirebaseAuth: false,
    userProfileColumnAssignments: false,
    supportCodes: false,
    addAndSetActions: false,
    showFileQuotas: false,
    enforceFileQuotas: false,
    fileTrackingV2Subsystem: false,
    fileTrackingV2TrackUploads: false,
    fileTrackingV2AutomaticDeletion: false,
    useGlideDateTime: false,
    exportData: false,
    showToastAction: false,
    stopwatch: false,
    scanCodeAction: false,
    playSound: false,
    promoCodesGloballyAvailable: false,
    allowUserDelete: false,
    floatingButton: false,
    uploadImageFromConfigurator: false,
    mediaLibrary: false,
    userAPIFetchColumn: false,
    dataEditorColumnSearch: false,
    webhookAction: false,
    addEditOnSubmitAction: false,
    deliverEmailAction: false,
    tinderArrayScreen: false,
    swipeCondition: false,
    liveScanLinearBarcode: false,
    constructURLColumn: false,
    requireUserOptInToSaveLogin: false,
    allowedEmailsFromUserProfile: false,
    multiColumnLayout: false,
    formFactorSwitch: false,
    inlinePlay: false,
    desktopFormFactor: false,
    duplicateTab: false,
    searchableDropdowns: false,
    emailLinkAuth: false,
    notebooks: false,
    drilldownCells: false,
    columnGrouping: false,
    findUsesOfColumn: false,
    askForBuilderLockOverride: false,
    publishingControl: false,
    nativeTableSnapshots: false,
    mapTabletLayout: false,
    offlineMode: true, // This is intentionally set to true
    offlineActionQueueForEverybody: false,
    copyAllComponents: false,
    firestorePersistenceInPWA: false,
    readOnlyBuilder: false,
    yesCodeColumn: false,
    debugLogging: false,
    pagesAdvert: false,
    allowLocationDataComponents: false,
    cloudinaryGlideOnly: false,
    unauthenticatedWebhooks: false,
    preferAppLoginTokens: true,
    cleanupAppLoginTokens: true,
    glidePages: false,
    glidePagesSignInWithGoogle: false,
    customCodeComponent: false,
    wireComponentHighlights: false,
    allowExcelSource: false,
    allowAirtableSource: false,
    formContainer: false,
    columnSH: false,
    aiAppCreator: false,
    appsOnNCM: false,
    billingVNoneCodePaths: false,
    noDefaultNewAppKind: false,
    dashboardPageTemplates: false,
    showPageTemplateSelector: false,
    pgMirrorFullyPreliminaryRows: false,
    reportThisApp: false,
};

export type FeatureSettings = { readonly [T in keyof typeof defaultFeatureSettings]: boolean | number | "experiment" };
export type FeatureSettingName = keyof FeatureSettings;

interface ChangeObservable<T> {
    subscribe(onChange: (newVal: T) => void): void;
    unsubscribe(onChange: (newVal: T) => void): void;
}

class Watchable<T> implements ChangeObservable<T> {
    private subscribers: Set<(val: T) => void> = new Set();

    constructor(private val: T) {}

    public get current(): T {
        return this.val;
    }
    public set current(newVal: T) {
        this.val = newVal;
        this.subscribers.forEach(cb => cb(newVal));
    }

    public subscribe(onChange: (newVal: T) => void): void {
        this.subscribers.add(onChange);
    }

    public unsubscribe(onChange: (newVal: T) => void) {
        this.subscribers.delete(onChange);
    }
}

const featureSettings = new Watchable<FeatureSettings>(defaultFeatureSettings);
export const featureSettingsObservable: ChangeObservable<FeatureSettings> = featureSettings;

const featureSettingsDocumentID = "features";
let didInitFeatureSettings = false;

let defaultAppID: string | undefined;

function getAppIDFraction(appID: string | undefined = defaultAppID): number {
    if (appID === undefined) return 1;

    let s = appID;
    let f = 1 / documentIDChars.length;
    let sum = 0;
    while (s.length > 0) {
        const idx = documentIDChars.indexOf(s[0]);
        if (idx < 0) return 1;

        sum += idx * f;

        s = s.substring(1);
        f /= documentIDChars.length;
    }

    return sum;
}

export function setAppIDForFeatureSettings(id: string): void {
    defaultAppID = id;
}

export function getFeatureSettingsInitialized(): boolean {
    return didInitFeatureSettings;
}

export function getFeatureSettingsFromDocumentData(data: DocumentData | undefined): FeatureSettings {
    return { ...defaultFeatureSettings, ...data };
}

export async function getFeatureSettingsFromDatabase(db: DataReader): Promise<FeatureSettings> {
    return getFeatureSettingsFromDocumentData(
        await db.getDocument(globalSettingsCollectionName, featureSettingsDocumentID)
    );
}

export function isFeatureSettingEnabled(settings: FeatureSettings, name: FeatureSettingName, appID?: string): boolean {
    const value = settings[name];
    if (value === "experiment") {
        return getFeatureFlag("showExperimental");
    }
    if (typeof value === "boolean") {
        return value;
    }
    return getAppIDFraction(appID) < value;
}

export function setFeatureSettings(fs: Partial<FeatureSettings>): FeatureSettings {
    didInitFeatureSettings = true;
    featureSettings.current = { ...defaultFeatureSettings, ...fs };
    return featureSettings.current;
}

export function listenFeatureSettings(
    db: Database,
    onFeatureSettingsChange: (featureSettings: FeatureSettings) => void
): void {
    db.listenToDocument(globalSettingsCollectionName, featureSettingsDocumentID, data => {
        featureSettings.current = getFeatureSettingsFromDocumentData(data);
        didInitFeatureSettings = true;

        onFeatureSettingsChange(featureSettings.current);
    });
}

export async function initFeatureSettings(db: DataReader): Promise<void> {
    if (didInitFeatureSettings) return;

    featureSettings.current = await getFeatureSettingsFromDatabase(db);
    didInitFeatureSettings = true;
}

export function getFeatureSettingProbability(name: FeatureSettingName): number {
    const value = featureSettings.current[name];
    if (value === "experiment") {
        return getFeatureFlag("showExperimental") ? 1 : 0;
    }
    if (typeof value === "boolean") {
        return value ? 1 : 0;
    }
    return value;
}

export function getFeatureSetting(name: FeatureSettingName, appID?: string): boolean {
    return isFeatureSettingEnabled(featureSettings.current, name, appID);
}

export function getBillingVNoneCodePaths(owner: Owner): boolean {
    if (getFeatureSetting("billingVNoneCodePaths", owner.id)) return true;
    if (owner.features?.billingVNoneCodePaths === true) return true;
    return false;
}
